import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, Select, DatePicker } from 'antd';
import { SearchOutlined, ClearOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { usePW9404Query } from './hooks/usePW9404Query';
import type { EmployeeRow } from '../../services/pw9404Api';
import './PW9404Form.css';

const { RangePicker } = DatePicker;

// Sex options matching FoxPro: 0=全部, 1=男, 2=女
const SEX_OPTIONS = [
  { value: 0, label: '全部' },
  { value: 1, label: '男' },
  { value: 2, label: '女' },
];

// Sort options matching BR-F01
const SORT_OPTIONS = [
  { value: 'empNo', label: '編號' },
  { value: 'dept', label: '部門代號' },
  { value: 'title', label: '職稱' },
  { value: 'seniority', label: '年資' },
];

const PW9404Form: React.FC = () => {
  const { loading, data, queried, noData, runQuery, reset } = usePW9404Query();

  // Filter state
  const [sex, setSex] = useState<number>(0);
  const [birthRange, setBirthRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);
  const [hireRange, setHireRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);
  const [deptFrom, setDeptFrom] = useState('');
  const [deptTo, setDeptTo] = useState('');
  const [gradeFrom, setGradeFrom] = useState('');
  const [gradeTo, setGradeTo] = useState('');
  const [sortBy, setSortBy] = useState<string>('empNo');

  // Auto-fill dept end (BR-U01)
  const handleDeptFromBlur = () => {
    if (deptFrom.trim() && !deptTo.trim()) {
      const padded = (deptFrom.trim() + 'zzzzzzzzzzzz').substring(0, 4);
      setDeptTo(padded);
    }
  };

  // Auto-fill grade end (BR-U02)
  const handleGradeFromBlur = () => {
    if (gradeFrom.trim() && !gradeTo.trim()) {
      const padded = (gradeFrom.trim() + 'zzzzzzzzzzzz').substring(0, 4);
      setGradeTo(padded);
    }
  };

  const handleQuery = () => {
    runQuery({
      sex,
      birthFrom: birthRange[0] ? birthRange[0].format('YYYY-MM-DD') : undefined,
      birthTo: birthRange[1] ? birthRange[1].format('YYYY-MM-DD') : undefined,
      hireDateFrom: hireRange[0] ? hireRange[0].format('YYYY-MM-DD') : undefined,
      hireDateTo: hireRange[1] ? hireRange[1].format('YYYY-MM-DD') : undefined,
      deptFrom: deptFrom.trim() || undefined,
      deptTo: deptTo.trim() || undefined,
      gradeFrom: gradeFrom.trim() || undefined,
      gradeTo: gradeTo.trim() || undefined,
      sortBy,
    });
  };

  const handleReset = () => {
    setSex(0);
    setBirthRange([null, null]);
    setHireRange([null, null]);
    setDeptFrom('');
    setDeptTo('');
    setGradeFrom('');
    setGradeTo('');
    setSortBy('empNo');
    reset();
  };

  const columns: ColumnsType<EmployeeRow> = [
    { title: '員工編號', dataIndex: 'emp_no', key: 'emp_no', width: 90, fixed: 'left' },
    { title: '姓名', dataIndex: 'emp_name', key: 'emp_name', width: 110 },
    { title: '性別', dataIndex: 'sex', key: 'sex', width: 60,
      render: (v: number) => v === 1 ? '男' : v === 2 ? '女' : '' },
    { title: '部門', dataIndex: 'dept_name', key: 'dept_name', width: 130,
      render: (name: string, row: EmployeeRow) => name || row.dept_code },
    { title: '職稱', dataIndex: 'title_name', key: 'title_name', width: 120,
      render: (name: string, row: EmployeeRow) => name || row.title_code },
    { title: '年資起算日', dataIndex: 'seniority_start', key: 'seniority_start', width: 110,
      render: (v: string) => v?.substring(0, 10) || '' },
    { title: '年資', dataIndex: 'seniority', key: 'seniority', width: 70,
      align: 'right',
      render: (v: number) => (typeof v === 'number' ? v.toFixed(1) : '') },
    { title: '職等', dataIndex: 'grade_code', key: 'grade_code', width: 70 },
    { title: '到職日', dataIndex: 'hire_date', key: 'hire_date', width: 110,
      render: (v: string) => v?.substring(0, 10) || '' },
    { title: '生日', dataIndex: 'birth_date', key: 'birth_date', width: 110,
      render: (v: string) => v?.substring(0, 10) || '' },
    { title: '分機', dataIndex: 'ext_tel', key: 'ext_tel', width: 80 },
  ];

  return (
    <PermissionGuard module={Modules.PW9404} action="query">
      <div className="pw9404-container">
        <div className="pw9404-header">
          <h2>員工資料查詢-總經理</h2>
        </div>

        <Card size="small" style={{ marginBottom: 16 }}>
          <div className="pw9404-filter-row">
            {/* 部門代號範圍 */}
            <div className="pw9404-filter-group">
              <label className="pw9404-filter-label">部門代號起</label>
              <Input
                value={deptFrom}
                onChange={e => setDeptFrom(e.target.value.toUpperCase())}
                onBlur={handleDeptFromBlur}
                placeholder="起始部門"
                maxLength={4}
                style={{ width: 100 }}
                allowClear
              />
            </div>
            <div className="pw9404-filter-group">
              <label className="pw9404-filter-label">部門代號迄</label>
              <Input
                value={deptTo}
                onChange={e => setDeptTo(e.target.value.toUpperCase())}
                placeholder="結束部門"
                maxLength={4}
                style={{ width: 100 }}
                allowClear
              />
            </div>

            {/* 職等範圍 */}
            <div className="pw9404-filter-group">
              <label className="pw9404-filter-label">職等起</label>
              <Input
                value={gradeFrom}
                onChange={e => setGradeFrom(e.target.value.toUpperCase())}
                onBlur={handleGradeFromBlur}
                placeholder="起始職等"
                maxLength={4}
                style={{ width: 100 }}
                allowClear
              />
            </div>
            <div className="pw9404-filter-group">
              <label className="pw9404-filter-label">職等迄</label>
              <Input
                value={gradeTo}
                onChange={e => setGradeTo(e.target.value.toUpperCase())}
                placeholder="結束職等"
                maxLength={4}
                style={{ width: 100 }}
                allowClear
              />
            </div>

            {/* 到職日範圍 */}
            <div className="pw9404-filter-group">
              <label className="pw9404-filter-label">到職日範圍</label>
              <RangePicker
                value={hireRange}
                onChange={vals => setHireRange(vals ? [vals[0], vals[1]] : [null, null])}
                style={{ width: 230 }}
                placeholder={['起始日期', '結束日期']}
              />
            </div>

            {/* 生日範圍 */}
            <div className="pw9404-filter-group">
              <label className="pw9404-filter-label">生日範圍</label>
              <RangePicker
                value={birthRange}
                onChange={vals => setBirthRange(vals ? [vals[0], vals[1]] : [null, null])}
                style={{ width: 230 }}
                placeholder={['起始日期', '結束日期']}
              />
            </div>

            {/* 性別 */}
            <div className="pw9404-filter-group">
              <label className="pw9404-filter-label">性別</label>
              <Select
                value={sex}
                onChange={setSex}
                options={SEX_OPTIONS}
                style={{ width: 90 }}
              />
            </div>

            {/* 排序方式 */}
            <div className="pw9404-filter-group">
              <label className="pw9404-filter-label">排序方式</label>
              <Select
                value={sortBy}
                onChange={setSortBy}
                options={SORT_OPTIONS}
                style={{ width: 120 }}
              />
            </div>

            {/* 查詢按鈕 */}
            <div className="pw9404-filter-actions">
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleQuery}
                  loading={loading}
                >
                  開始查詢
                </Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>
                  清除
                </Button>
              </Space>
            </div>
          </div>
        </Card>

        {queried && noData && (
          <Empty description="查無符合條件的員工資料" style={{ marginTop: 40 }} />
        )}
        {queried && !noData && (
          <Table<EmployeeRow>
            columns={columns}
            dataSource={data}
            rowKey="emp_no"
            loading={loading}
            size="small"
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              showTotal: t => `共 ${t} 筆`,
            }}
            scroll={{ x: 1100 }}
          />
        )}
      </div>
    </PermissionGuard>
  );
};

export default PW9404Form;
